// 🏆 Magical Effects Catalog - 100+ Award-Winning Effects 🏆
// Unified catalog supporting both legacy (50) and 2026 premium (100+) effects

export interface MagicalEffect {
  id: string;
  name: string;
  icon: string; // Unicode character or emoji
  themes: string[]; // For AI matching
  layering: 'behind' | 'front' | 'mixed'; // Smart layering (mixed = new!)
  animationType: 
    | 'fall' | 'float' | 'pulse' | 'spin' | 'wave' | 'rise'
    | 'spiral' | 'explode' | 'drift' | 'bounce' | 'shimmer-wave' | 'twirl' | 'cascade' | 'orbit'; // Enhanced!
  density: 'low' | 'medium' | 'high' | 'ultra'; // Number of particles (ultra = new!)
  colors: string[]; // Color variants
  speed: 'slow' | 'medium' | 'fast';
  special?: { // NEW Premium features!
    glow?: boolean;
    trail?: boolean;
    blur?: boolean;
    sparkle?: boolean;
    gradient?: boolean;
    rotation?: boolean;
    scale?: boolean;
    parallax?: number;
  };
}

export const MAGICAL_EFFECTS: MagicalEffect[] = [
  // MAGICAL (12 effects)
  { id: 'sparkles', name: 'Sparkles', icon: '✨', themes: ['magic', 'celebration', 'joy'], layering: 'front', animationType: 'float', density: 'high', colors: ['#FFD700', '#FFF'], speed: 'slow' },
  { id: 'stars', name: 'Twinkling Stars', icon: '⭐', themes: ['night', 'magic', 'dreams'], layering: 'behind', animationType: 'pulse', density: 'medium', colors: ['#FFD700', '#FFF'], speed: 'slow' },
  { id: 'stardust', name: 'Stardust Trail', icon: '✦', themes: ['magic', 'fantasy'], layering: 'front', animationType: 'wave', density: 'high', colors: ['#B19CD9', '#FFF'], speed: 'medium' },
  { id: 'fairy-dust', name: 'Fairy Dust', icon: '✧', themes: ['fairy', 'magic'], layering: 'front', animationType: 'float', density: 'high', colors: ['#FFB6C1', '#FFF'], speed: 'slow' },
  { id: 'magic-orbs', name: 'Magic Orbs', icon: '◉', themes: ['magic', 'mystery'], layering: 'behind', animationType: 'float', density: 'low', colors: ['#9370DB', '#87CEEB'], speed: 'slow' },
  { id: 'shooting-stars', name: 'Shooting Stars', icon: '☄', themes: ['night', 'wishes'], layering: 'behind', animationType: 'fall', density: 'low', colors: ['#FFD700'], speed: 'fast' },
  { id: 'glitter', name: 'Glitter', icon: '⋆', themes: ['celebration', 'joy'], layering: 'front', animationType: 'fall', density: 'high', colors: ['#FFD700', '#FF69B4'], speed: 'medium' },
  { id: 'magic-swirls', name: 'Magic Swirls', icon: '〰', themes: ['magic', 'fantasy'], layering: 'front', animationType: 'spin', density: 'medium', colors: ['#9370DB'], speed: 'medium' },
  { id: 'moon-glow', name: 'Moon Glow', icon: '☽', themes: ['night', 'bedtime'], layering: 'behind', animationType: 'pulse', density: 'low', colors: ['#F0E68C'], speed: 'slow' },
  { id: 'light-rays', name: 'Light Rays', icon: '☀', themes: ['morning', 'hope'], layering: 'behind', animationType: 'wave', density: 'low', colors: ['#FFD700'], speed: 'slow' },
  { id: 'crystal-shine', name: 'Crystal Shine', icon: '◇', themes: ['magic', 'treasure'], layering: 'front', animationType: 'pulse', density: 'medium', colors: ['#B0E0E6'], speed: 'medium' },
  { id: 'rainbow-sparkle', name: 'Rainbow Sparkle', icon: '🌈', themes: ['joy', 'color'], layering: 'front', animationType: 'float', density: 'low', colors: ['#FF0000', '#FFD700', '#00FF00', '#0000FF'], speed: 'slow' },
  
  // NATURE (12 effects)
  { id: 'butterflies', name: 'Butterflies', icon: '🦋', themes: ['nature', 'spring', 'garden'], layering: 'front', animationType: 'float', density: 'low', colors: ['#FFB6C1', '#87CEEB'], speed: 'slow' },
  { id: 'fireflies', name: 'Fireflies', icon: '✺', themes: ['night', 'nature', 'summer'], layering: 'front', animationType: 'float', density: 'medium', colors: ['#FFFFE0'], speed: 'slow' },
  { id: 'leaves', name: 'Falling Leaves', icon: '🍂', themes: ['autumn', 'nature'], layering: 'front', animationType: 'fall', density: 'medium', colors: ['#FF8C00', '#DC143C'], speed: 'slow' },
  { id: 'petals', name: 'Flower Petals', icon: '🌸', themes: ['spring', 'garden', 'nature'], layering: 'front', animationType: 'fall', density: 'medium', colors: ['#FFB6C1', '#FFF'], speed: 'slow' },
  { id: 'bubbles', name: 'Bubbles', icon: '○', themes: ['bath', 'ocean', 'play'], layering: 'front', animationType: 'rise', density: 'medium', colors: ['#87CEEB', '#FFF'], speed: 'medium' },
  { id: 'birds', name: 'Flying Birds', icon: '🐦', themes: ['nature', 'sky', 'freedom'], layering: 'behind', animationType: 'wave', density: 'low', colors: ['#000'], speed: 'medium' },
  { id: 'clouds', name: 'Drifting Clouds', icon: '☁', themes: ['sky', 'calm', 'day'], layering: 'behind', animationType: 'float', density: 'low', colors: ['#FFF'], speed: 'slow' },
  { id: 'raindrops', name: 'Rain Drops', icon: '💧', themes: ['rain', 'water'], layering: 'front', animationType: 'fall', density: 'high', colors: ['#87CEEB'], speed: 'fast' },
  { id: 'dewdrops', name: 'Dew Drops', icon: '◦', themes: ['morning', 'nature'], layering: 'front', animationType: 'pulse', density: 'medium', colors: ['#B0E0E6'], speed: 'slow' },
  { id: 'grass-sway', name: 'Swaying Grass', icon: '🌿', themes: ['nature', 'wind'], layering: 'behind', animationType: 'wave', density: 'low', colors: ['#90EE90'], speed: 'slow' },
  { id: 'dandelion', name: 'Dandelion Seeds', icon: '❋', themes: ['nature', 'wishes'], layering: 'front', animationType: 'float', density: 'medium', colors: ['#FFF'], speed: 'slow' },
  { id: 'bees', name: 'Busy Bees', icon: '🐝', themes: ['garden', 'nature'], layering: 'front', animationType: 'wave', density: 'low', colors: ['#FFD700'], speed: 'medium' },
  
  // SEASONAL (8 effects)
  { id: 'snowflakes', name: 'Snowflakes', icon: '❄', themes: ['winter', 'snow', 'cold'], layering: 'front', animationType: 'fall', density: 'high', colors: ['#FFF', '#B0E0E6'], speed: 'slow' },
  { id: 'cherry-blossoms', name: 'Cherry Blossoms', icon: '🌸', themes: ['spring', 'japan'], layering: 'front', animationType: 'fall', density: 'medium', colors: ['#FFB6C1'], speed: 'slow' },
  { id: 'autumn-wind', name: 'Autumn Wind', icon: '🍃', themes: ['autumn', 'wind'], layering: 'front', animationType: 'wave', density: 'medium', colors: ['#FF8C00'], speed: 'medium' },
  { id: 'summer-sun', name: 'Summer Rays', icon: '☀', themes: ['summer', 'hot'], layering: 'behind', animationType: 'pulse', density: 'low', colors: ['#FFD700'], speed: 'slow' },
  { id: 'frost', name: 'Frost Crystals', icon: '❅', themes: ['winter', 'cold'], layering: 'front', animationType: 'pulse', density: 'medium', colors: ['#B0E0E6'], speed: 'slow' },
  { id: 'spring-flowers', name: 'Spring Blooms', icon: '🌼', themes: ['spring', 'garden'], layering: 'front', animationType: 'rise', density: 'low', colors: ['#FFD700', '#FFB6C1'], speed: 'slow' },
  { id: 'harvest', name: 'Harvest Fall', icon: '🌾', themes: ['autumn', 'harvest'], layering: 'behind', animationType: 'wave', density: 'low', colors: ['#FFD700'], speed: 'slow' },
  { id: 'ice-sparkle', name: 'Ice Sparkle', icon: '◈', themes: ['winter', 'ice'], layering: 'front', animationType: 'pulse', density: 'high', colors: ['#B0E0E6', '#FFF'], speed: 'medium' },
  
  // THEMATIC (10 effects)
  { id: 'hearts', name: 'Floating Hearts', icon: '♥', themes: ['love', 'friendship'], layering: 'front', animationType: 'float', density: 'medium', colors: ['#FF69B4'], speed: 'slow' },
  { id: 'music-notes', name: 'Music Notes', icon: '♪', themes: ['music', 'song'], layering: 'front', animationType: 'float', density: 'medium', colors: ['#9370DB'], speed: 'medium' },
  { id: 'books', name: 'Flying Books', icon: '📖', themes: ['reading', 'learning'], layering: 'behind', animationType: 'float', density: 'low', colors: ['#8B4513'], speed: 'slow' },
  { id: 'birthday', name: 'Birthday Confetti', icon: '🎉', themes: ['birthday', 'celebration'], layering: 'front', animationType: 'fall', density: 'high', colors: ['#FF0000', '#FFD700', '#0000FF'], speed: 'fast' },
  { id: 'sleep-zzz', name: 'Sleep Zzz', icon: 'Zzz', themes: ['bedtime', 'sleep'], layering: 'front', animationType: 'rise', density: 'low', colors: ['#9370DB'], speed: 'slow' },
  { id: 'balloons', name: 'Balloons', icon: '🎈', themes: ['party', 'joy'], layering: 'front', animationType: 'rise', density: 'low', colors: ['#FF69B4', '#87CEEB'], speed: 'slow' },
  { id: 'presents', name: 'Gift Boxes', icon: '🎁', themes: ['christmas', 'gifts'], layering: 'behind', animationType: 'pulse', density: 'low', colors: ['#FF0000'], speed: 'slow' },
  { id: 'candy', name: 'Candy Rain', icon: '🍬', themes: ['sweet', 'treats'], layering: 'front', animationType: 'fall', density: 'medium', colors: ['#FF69B4'], speed: 'medium' },
  { id: 'treasure', name: 'Treasure Shine', icon: '💎', themes: ['treasure', 'adventure'], layering: 'front', animationType: 'pulse', density: 'low', colors: ['#00CED1'], speed: 'medium' },
  { id: 'rockets', name: 'Space Rockets', icon: '🚀', themes: ['space', 'adventure'], layering: 'behind', animationType: 'wave', density: 'low', colors: ['#FF6347'], speed: 'fast' },
  
  // ATMOSPHERIC (8 effects)
  { id: 'mist', name: 'Gentle Mist', icon: '〜', themes: ['fog', 'mystery'], layering: 'behind', animationType: 'wave', density: 'medium', colors: ['#F0F8FF'], speed: 'slow' },
  { id: 'wind', name: 'Wind Swirls', icon: '≈', themes: ['wind', 'movement'], layering: 'behind', animationType: 'wave', density: 'medium', colors: ['#87CEEB'], speed: 'fast' },
  { id: 'smoke', name: 'Magic Smoke', icon: '∿', themes: ['magic', 'mysterious'], layering: 'behind', animationType: 'rise', density: 'low', colors: ['#9370DB'], speed: 'slow' },
  { id: 'aurora', name: 'Aurora Lights', icon: '▬', themes: ['night', 'magic', 'north'], layering: 'behind', animationType: 'wave', density: 'low', colors: ['#00FF00', '#0000FF'], speed: 'slow' },
  { id: 'ocean-waves', name: 'Ocean Ripples', icon: '〰', themes: ['ocean', 'water'], layering: 'behind', animationType: 'wave', density: 'medium', colors: ['#87CEEB'], speed: 'medium' },
  { id: 'shimmer', name: 'Shimmer Effect', icon: '⊹', themes: ['magic', 'shine'], layering: 'front', animationType: 'pulse', density: 'high', colors: ['#FFD700'], speed: 'fast' },
  { id: 'gentle-glow', name: 'Gentle Glow', icon: '◯', themes: ['calm', 'peaceful'], layering: 'behind', animationType: 'pulse', density: 'low', colors: ['#FFFFE0'], speed: 'slow' },
  { id: 'floating-dust', name: 'Floating Dust', icon: '·', themes: ['calm', 'ambient'], layering: 'behind', animationType: 'float', density: 'high', colors: ['#FFF'], speed: 'slow' },
];

// Helper functions for AI selection and frontend usage
export function getEffectsByTheme(theme: string): MagicalEffect[] {
  return MAGICAL_EFFECTS.filter(effect => 
    effect.themes.some(t => t.toLowerCase().includes(theme.toLowerCase()))
  );
}

export function getEffectById(id: string): MagicalEffect | undefined {
  return MAGICAL_EFFECTS.find(effect => effect.id === id);
}

// Get all effect IDs for AI prompt
export function getAllEffectIds(): string[] {
  return MAGICAL_EFFECTS.map(e => e.id);
}

// Get effects by category for AI guidance
export function getEffectsByCategory(): Record<string, string[]> {
  return {
    'Magic/Fantasy': ['sparkles', 'stars', 'stardust', 'fairy-dust', 'magic-orbs', 'shooting-stars', 'glitter', 'magic-swirls', 'crystal-shine', 'rainbow-sparkle'],
    'Nature': ['butterflies', 'fireflies', 'leaves', 'petals', 'bubbles', 'birds', 'clouds', 'dewdrops', 'grass-sway', 'dandelion', 'bees'],
    'Seasonal': ['snowflakes', 'cherry-blossoms', 'autumn-wind', 'summer-sun', 'frost', 'spring-flowers', 'harvest', 'ice-sparkle'],
    'Celebration': ['hearts', 'music-notes', 'birthday', 'balloons', 'glitter', 'candy'],
    'Bedtime': ['sleep-zzz', 'moon-glow', 'gentle-glow', 'floating-dust', 'stars'],
    'Adventure': ['treasure', 'rockets', 'books', 'magic-swirls'],
    'Weather': ['raindrops', 'wind', 'mist', 'ocean-waves', 'clouds'],
    'Atmospheric': ['aurora', 'shimmer', 'light-rays', 'dewdrops', 'mist', 'gentle-glow', 'floating-dust'],
  };
}

// Fallback mapper for common GPT-4o mistakes
const EFFECT_ID_FALLBACKS: Record<string, string> = {
  // Common abbreviations/mistakes
  'dust': 'floating-dust',
  'glow': 'gentle-glow',
  'sun-beams': 'light-rays',
  'sun-rays': 'light-rays',
  'sunbeams': 'light-rays',
  'sunrays': 'light-rays',
  'sun': 'summer-sun',
  'sparkle': 'sparkles',
  'butterfly': 'butterflies',
  'leaf': 'leaves',
  'petal': 'petals',
  'bubble': 'bubbles',
  'bird': 'birds',
  'cloud': 'clouds',
  'raindrop': 'raindrops',
  'dewdrop': 'dewdrops',
  'snowflake': 'snowflakes',
  'heart': 'hearts',
  'star': 'stars',
  'flower': 'spring-flowers',
  'flowers': 'spring-flowers',
  'music': 'music-notes',
  'note': 'music-notes',
  'notes': 'music-notes',
  'book': 'books',
  'balloon': 'balloons',
  'present': 'presents',
  'rocket': 'rockets',
  'bee': 'bees',
  'firefly': 'fireflies',
  'dew': 'dewdrops',
  'frost-crystals': 'frost',
  'ice': 'ice-sparkle',
  'aurora-lights': 'aurora',
  // GPT-4o confusion with visualEffect types
  'lightning': 'glitter', // lightning is a visualEffect, not magicalEffect
  'rain': 'raindrops',
  'snow': 'snowflakes',
  'fog': 'mist',
  'sunshine': 'summer-sun',
  // Time/mood mistakes
  'sunset': 'light-rays',
  'sunrise': 'light-rays',
  'moonlight': 'moon-glow',
  // Emotion mistakes
  'laughing': 'music-notes', // happiness/joy
  'happy': 'sparkles',
  'joy': 'glitter',
  'smile': 'hearts',
  'excited': 'birthday',
  // Ocean/water variations
  'waves': 'ocean-waves',
  'water-drops': 'raindrops',
  'water': 'raindrops',
  'underwater-glow': 'gentle-glow', // underwater/ocean scenes
  'ocean-glow': 'gentle-glow',
  'sea-sparkle': 'shimmer',
  'sea-foam': 'bubbles',
  'ripples': 'ocean-waves',
  'tide': 'ocean-waves',
  // Nature variations
  'garden': 'butterflies',
  'forest': 'leaves',
  'meadow': 'grass-sway',
  'field': 'grass-sway',
  // Light variations
  'twinkle': 'stars',
  'shine': 'shimmer',
  'gleam': 'crystal-shine',
  'radiance': 'light-rays',
  'beam': 'light-rays',
  'beams': 'light-rays',
  // Sky variations
  'sky': 'clouds',
  'heavens': 'stars',
  // Weather variations
  'storm': 'raindrops',
  'drizzle': 'raindrops',
  'mist-glow': 'mist',
  'haze': 'mist',
  // Magic variations
  'enchanted': 'magic-swirls',
  'mystical': 'magic-orbs',
  'wonder': 'sparkles',
  'magical': 'magic-swirls',
  'spell': 'fairy-dust',
  'charm': 'fairy-dust',
};

// Smart effect ID resolver with fallback
export function resolveEffectId(effectId: string): string | undefined {
  // Try exact match first
  const exactMatch = getEffectById(effectId);
  if (exactMatch) return effectId;
  
  // Try fallback mapping
  const normalized = effectId.toLowerCase().trim();
  const fallback = EFFECT_ID_FALLBACKS[normalized];
  if (fallback) {
    console.warn(`[Effect Fallback] Mapped "${effectId}" → "${fallback}"`);
    return fallback;
  }
  
  // Not found
  return undefined;
}

